import React, { Dispatch } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import DashedLine from 'react-native-dashed-line';

import { font } from '@styles/fonts';

import TouchableElement from '@components/common/touchableElement';
import { StatisticsPerPeriod } from '@components/ncFiles/components/paid';
import { ValueName } from '@components/ncFiles/components/paid/components/barChart';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useManagePaidBarChartBar } from '@components/ncFiles/components/paid/components/barChart/hooks/useManagePaidBarChartBar';
import { RenderChart } from '@components/ncFiles/components/paid';

export default ({
  item,
  padding,
  chartHeight,
  chartWidth,
  getData,
  id,
  idKey = 'ncFileId',
  barBlockWidth,
  barWidth,
  C,
  saveChartData,
  selectedDate,
  barValueName,
  lineValueName,
  lineColor,
  barColor,
  barAmount,
  average,
  averagePaid,
  showAverage,
  selectedBarIndex,
  setSelectedBar,
  setSelectedBarIndex,
}: {
  item: RenderChart;
  padding: number;
  chartHeight: number;
  chartWidth: number;
  getData: any;
  id: number;
  idKey: string;
  barBlockWidth: number;
  barWidth: number;
  C: any;
  saveChartData: (date: string, data: any) => void;
  selectedDate: string;
  barValueName: ValueName;
  lineValueName: ValueName;
  lineColor: string;
  barColor: string;
  barAmount: number;
  average: number;
  averagePaid: number;
  showAverage: boolean;
  selectedBarIndex: number | null;
  setSelectedBar: Dispatch<React.SetStateAction<StatisticsPerPeriod | null>>;
  setSelectedBarIndex: Dispatch<React.SetStateAction<number | null>>;
}) => {
  const { legend, legendRules, bottomLegendBlockWidth, getPosition } = useManagePaidBarChartBar({
    item,
    id,
    idKey,
    barValueName,
    lineValueName,
    average,
    averagePaid,
    showAverage,
    barAmount,
    chartWidth,
    selectedDate,
    getData,
    saveChartData,
    barBlockWidth,
    barWidth,
    chartHeight,
    padding,
    lineColor,
    barColor,
  });

  return (
    <View>
      <View
        testID={TEST_IDS.paidBarChartBar.chartContainer}
        style={{
          width: chartWidth,
          paddingHorizontal: padding,
          height: chartHeight,
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
        key={`chart-${item?.date}`}
      >
        {item?.data?.data?.statisticsPerPeriod?.map((el, index) => {
          const isSelected = selectedBarIndex === index;
          return (
            <TouchableElement
              key={`bar-${index}`}
              hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
              onPress={() => {
                if (isSelected) {
                  setSelectedBarIndex(null);
                  setSelectedBar(null);
                } else {
                  setSelectedBarIndex(index);
                  el.height = getPosition(el[barValueName]);
                  setSelectedBar(el);
                }
              }}
            >
              {({ boxStyle }: { boxStyle: any }) => {
                return (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: '100%',
                        flexDirection: 'row',
                        alignItems: 'flex-end',
                        width: barBlockWidth,
                        position: 'absolute',
                        zIndex: 1000,
                        left: index * bottomLegendBlockWidth,
                        opacity: selectedBarIndex !== null && !isSelected ? 0.5 : 1,
                      },
                    ]}
                  >
                    {Boolean(el[lineValueName]) && (
                      <View
                        style={[
                          {
                            width: barBlockWidth,
                            height: 3,
                            borderRadius: 2,
                            backgroundColor: C(lineColor),
                            position: 'absolute',
                            bottom: `${getPosition(el[lineValueName]) - 1}%`,
                          },
                        ]}
                      />
                    )}
                    <View
                      style={[
                        {
                          width: barBlockWidth,
                          height: `${getPosition(el[barValueName])}%`,
                          minHeight: el[barValueName] ? 1 : 0,
                          alignItems: 'center',
                        },
                      ]}
                    >
                      {item?.data?.loaded ? (
                        <View
                          style={[
                            boxStyle,
                            {
                              width: barWidth,
                              height: '100%',
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              borderTopLeftRadius: 2,
                              borderTopRightRadius: 2,
                              overflow: 'hidden',
                              backgroundColor: C(barColor),
                            },
                          ]}
                        ></View>
                      ) : (
                        <View
                          nativeID="shimmer"
                          style={{
                            width: barWidth,
                            height: '100%',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            borderTopLeftRadius: 2,
                            borderTopRightRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: C('Gray/Light/3'),
                          }}
                        ></View>
                      )}
                    </View>
                  </View>
                );
              }}
            </TouchableElement>
          );
        })}
        {showAverage && (
          <DashedLine
            axis="horizontal"
            style={{ width: chartWidth, height: 1, position: 'absolute', bottom: `${getPosition(averagePaid) - 1}%` }}
            dashThickness={StyleSheet.hairlineWidth}
            dashLength={2}
            dashGap={2}
            dashColor={C(lineColor)}
          />
        )}
        {showAverage && (
          <DashedLine
            axis="horizontal"
            style={{ width: chartWidth, height: 1, position: 'absolute', bottom: `${getPosition(average) - 1}%` }}
            dashThickness={StyleSheet.hairlineWidth}
            dashLength={2}
            dashGap={2}
            dashColor={C(barColor)}
          />
        )}
      </View>
      {legend.map((legendItem, index) => (
        <View
          key={`legend-${index}-${legendItem?.date}`}
          style={{
            position: 'absolute',
            top: 0,
            width: bottomLegendBlockWidth,
            left: index * bottomLegendBlockWidth,
            height: 165,
          }}
        >
          {legendRules.includes(legendItem) ? (
            <>
              <View
                style={{
                  position: 'absolute',
                  width: StyleSheet.hairlineWidth,
                  height: '100%',
                  left: -StyleSheet.hairlineWidth / 2,
                }}
              >
                <DashedLine
                  axis="vertical"
                  style={{ height: 165 }}
                  dashThickness={StyleSheet.hairlineWidth}
                  dashLength={2}
                  dashGap={2}
                  dashColor={C('Label/Light/Quaternary')}
                />
              </View>
              <Text
                numberOfLines={1}
                style={[
                  font.Footnote,
                  {
                    color: C('Label/Light/Quaternary'),
                    position: 'absolute',
                    bottom: -3,
                    left: (barBlockWidth - barWidth) / 2,
                    minWidth: 20,
                  },
                ]}
              >
                {i18n.t(String(legendItem))}
              </Text>
            </>
          ) : null}
          {index === legend.length - 1 && (
            <View
              style={{
                position: 'absolute',
                width: StyleSheet.hairlineWidth,
                height: '100%',
                right: 0,
              }}
            >
              <DashedLine
                axis="vertical"
                style={{ height: 165 }}
                dashThickness={StyleSheet.hairlineWidth}
                dashLength={2}
                dashGap={2}
                dashColor={C('Label/Light/Quaternary')}
              />
            </View>
          )}
        </View>
      ))}
    </View>
  );
};
